import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { clientSettings } from 'go-modules/models/common/client.settings';
import { firstValueFrom, Observable } from 'rxjs';
import type { Activity, ActivitySyncChildrenCount, TaskTitleAndDescription } from '@go-modules/interfaces/activity';
import { UADetect as UADetectClass, uaDetectToken } from 'go-modules/detect/ua-detect.service';
import { activityToken } from 'go-modules/models/activity/activity.factory';
import { PrivacyLevel } from 'go-modules/models/activity';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { ActivityValidationResult, SessionValidationResult } from 'go-modules/models/group-dep/group.factory';
import {
	ActivityEditorPanel,
	activityEditorPanelToken
} from 'go-modules/activity-editor-panel/activity-editor-panel.service';
import { MasterActivityContactDetails } from '@go-modules/interfaces/master-activity-contact-details';
import type { AiUseType } from '@go-modules/interfaces/ai-use-type';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { ActivityModel } from 'go-modules/models/activity/activity.schema';
import { GoSidepanelService } from '@go-modules/services/go-sidepanel/go-sidepanel.service';
import { NgxActivityEditPanelComponent } from '@go-modules/components/sidepanels/activity-editor/ngx-activity-edit-panel/ngx-activity-edit-panel.component';
import type { ActivityReport } from '@go-modules/interfaces/activity-summary-report';
import type { Task } from '@go-modules/interfaces/task';
import { TaskType } from '@go-modules/interfaces/task';
import { escapeHtml } from '@go-modules/common/helpers/utils';
import { activityTemplateModelToken } from 'go-modules/models/activity-template/activity-template.factory';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';
import { IPromise } from 'angular';
import { ActivityTaskOverviewAndPaneComponent } from '@go-modules/components/activity-task-overview-and-pane/activity-task-overview-and-pane.component';

export interface DefaultActivity {
	aiUseType: AiUseType | null;
	activity: Activity;
}

@Injectable({
	providedIn: 'root'
})
export class NgxActivityService {
	constructor (
		private http: HttpClient,
		private translate: TranslateService,
		public environmentVarsService: EnvironmentVarsService,
		private goSidepanelService: GoSidepanelService,
		private featureFlag: NgxFeatureFlagService,
		@Inject(selectedServiceToken) public selectedService: SelectedService,
		@Inject(userServiceToken) public userService: UserService,
		@Inject(uaDetectToken) public UADetect: UADetectClass,
		@Inject(activityToken) public activityFactory,
		@Inject(activityEditorPanelToken) private activityEditorPanel: ActivityEditorPanel,
		@Inject(activityTemplateModelToken) private ActivityTemplateModel
	) {}

	public get (activityId: number): Observable<Activity> {
		return this.http.get<Activity>(`${clientSettings.GoReactV2API}/activities/${activityId}`);
	}

	public getByGroup (groupId: number): Observable<Activity[]> {
		return this.http.get<Activity[]>(`${clientSettings.GoReactV2API}/groups/${groupId}/activities`);
	}

	public getDefault (): Observable<DefaultActivity> {
		return this.http.get<DefaultActivity>(`${clientSettings.GoReactV2API}/users/self/activity/defaults`);
	}

	public updateAiMarkersDefault (ai_use_type_id: number|null): Observable<any> {
		return this.http.patch(`${clientSettings.GoReactV2API}/user-activity-default`, {ai_use_type_id});
	}

	public getStats (groupId: number): Observable<any[]> {
		return this.http.get<any[]>(`${clientSettings.GoReactV2API}/groups/${groupId}/activity-stats`);
	}

	public restore (activityId: number): Observable<any> {
		return this.http.patch(`${clientSettings.GoReactV2API}/activities/${activityId}/restore`, {});
	}

	public archive (activityId: number): Observable<any> {
		return this.http.patch(`${clientSettings.GoReactV2API}/activities/${activityId}/archive`, {});
	}

	public toggleShowTasks (activityId: number, showTasks: boolean): Observable<Activity> {
		return this.http.patch<Activity>(
			`${clientSettings.GoReactV2API}/activities/${activityId}/show-tasks`,
			{ show_tasks: showTasks }
		);
	}

	public deleteActivity (activityId: number): Observable<any> {
		return this.http.delete(`${clientSettings.GoReactV2API}/activities/${activityId}`);
	}

	public updateSort (groupId: number, payload: any): Observable<any> {
		return this.http.put(`${clientSettings.GoReactV2API}/groups/${groupId}/activities/sort`, payload);
	}

	public getMasterActivityDetails (activityId: number): Observable<MasterActivityContactDetails> {
		return this.http.get<MasterActivityContactDetails>(`${clientSettings.GoReactV2API}/activities/${activityId}/creator`);
	}

	public getChildrenCount (activityId: number): Observable<ActivitySyncChildrenCount> {
		return this.http.get<ActivitySyncChildrenCount>(`${clientSettings.GoReactV2API}/activities/${activityId}/children-count`);
	}

	public save (activity: Activity|ActivityModel): Observable<Activity> {
		return this.http.post<Activity>(`${clientSettings.GoReactV2API}/activities`, activity);
	}

	public update (activity: Activity|ActivityModel): Observable<Activity> {
		return this.http.put<Activity>(`${clientSettings.GoReactV2API}/activities/${activity.activity_id}`, activity);
	}

	public getSummaryReport (activityId: number, groupId: number) {
		let params = new HttpParams();

		if (activityId !== null) {
			params = params.set('activity_id', activityId.toString());
		}

		return this.http.get<ActivityReport[]>(`${clientSettings.GoReactV2API}/groups/${groupId}/activity-report`, { params });
	}

	public getTaskCompletionReport (activityId: number): Observable<string> {
		const headers = new HttpHeaders({
			Accept: 'text/csv'
		});
		return this.http.get(`${clientSettings.GoReactV2API}/activities/${activityId}/task_completion_report`, {
			headers,
			responseType: 'text'
		});
	}

	public canStartActivity (): boolean {
		try {
			// This activity check is only present on vertical nav Start Activity buttons
			this.mayStartActivity();
		} catch (error)	{
			return false;
		}

		const group = this.selectedService.getGroup();
		const license = this.selectedService.getLicense();
		return group.validateCanCreateSession(this.userService.currentUser, license).isValid;
	}

	public canEditActivity (activity) {
		const org = this.selectedService.getOrg();
		return !(org.org_settings.block_copied_assignment_editing
			&& !!activity.parent_id
			&& !this.userService.currentUser.is_root_user);
	}

	public shouldUseNgxActivityEditor (activity): boolean {
		return this.featureFlag.isAvailable('ACTIVITY_TASKS') && activity.show_tasks === true;
	}

	public openActivityEditor (activity, limitActivityEditor = false): Promise<ActivityModel | void> | IPromise<void> {
		// Check if we should use the new NGX editor
		if (this.shouldUseNgxActivityEditor(activity)) {
			return firstValueFrom(this.openNgxActivityEditor(activity, limitActivityEditor))
				.then((result) => {
					if (result) {
						return result;
					}
					return Promise.reject();
				});
		}

		// Activity editor header options
		const headerOptions: any = {
			editMode: false
		};
		// When we are in the LTI environment and in the default
		// mode, we need to limit the activity editor options.
		if (limitActivityEditor) {
			headerOptions.hideName = true;
		}

		if (!!activity.activity_id) {
			headerOptions.editMode = true;
		}

		return this.activityEditorPanel.open({
			user: this.userService.currentUser,
			activity,
			group: this.selectedService.getGroup(),
			orgSettings: this.selectedService.getOrg().org_settings,
			headerOptions,
			firstFocusSelector: '#activity-editor-name-input'
		}).result;
	}

	public openNgxActivityEditor (activity, limitActivityEditor = false): Observable<ActivityModel | void> {
		// Activity editor header options
		const headerOptions: any = {
			editMode: false
		};
		// When we are in the LTI environment and in the default
		// mode, we need to limit the activity editor options.
		if (limitActivityEditor) {
			headerOptions.hideName = true;
		}

		if (!!activity.activity_id) {
			headerOptions.editMode = true;
		}

		const dialogRef = this.goSidepanelService.open(NgxActivityEditPanelComponent, {
			noFooter: true,
			inputs: {
				activity,
				headerOptions
			}
		});
		return dialogRef.afterClosed();
	}

	public openActivityInfoPanel ()
	{
		const dialogRef = this.goSidepanelService.open(ActivityTaskOverviewAndPaneComponent, {
			noFooter: true,
			panelClass: ['animated', 'activity-info-overview'],
			title: ' ',
			inputs: {}
		});
		return dialogRef.afterClosed();
	}

	public getSessionCreationDisabledMessage (): string {
		try {
			// This activity check is only present on vertical nav Start Activity buttons
			this.mayStartActivity();
		} catch (error) {
			return error.message;
		}

		const group = this.selectedService.getGroup();
		const license = this.selectedService.getLicense();
		const validationResult: SessionValidationResult =
			group.validateCanCreateSession(this.userService.currentUser, license);

		return validationResult.isValid ? '' : this.translate.instant(validationResult.errorKey);
	}

	public shouldHideSourceMedia (activity: Activity|ActivityModel): boolean {
		return this.selectedService.getGroup().hasPresenterRole() &&
			activity.has_single_recording_attempt &&
			!activity.have_i_submitted;
	}

	private mayStartActivity (): boolean {
		// TODO DEV-15860 Messaging can be incorrect before stats load
		const activity = this.activityFactory.model(this.selectedService.getActivity());
		const group = this.selectedService.getGroup();
		const isOpenPeerReview = activity.getPrivacyLevel() === PrivacyLevel.OPEN_PEER_REVIEW;

		if (this.environmentVarsService.get(EnvironmentVarsService.VAR.READONLY)) {
			throw new Error(this.translate.instant('session-creation-error_read-only-mode'));
		} else if (activity.isConversation() && this.UADetect.browserDetector.isOldEdge()) {
			throw new Error(this.translate.instant('common_unsupported-feature-try-latest-browser-version'));
		} else if (group.hasReviewerRole()) {
			throw new Error(this.translate.instant('session-creation-error_group-reviewer-may-not-create'));
		} else if (group.hasPresenterRole() && activity.has_single_recording_attempt && activity.have_i_submitted) {
			throw new Error(this.translate.instant('session-creation-error_activity-test-already-submitted'));
		} else if (group.hasPresenterRole() && activity.isCommentOnly() && activity.have_i_submitted) {
			throw new Error(this.translate.instant('session-creation-error_activity-comment-only-already-submitted'));
		} else if (isOpenPeerReview && group.hasInstructorRole(true) && activity.isCommentOnly()) {
			throw new Error(this.translate.instant('session-creation-error_activity-comment-only-instructor'));
		}

		return true;
	}

	public canCreateActivity (): boolean {
		const group = this.selectedService.getGroup();
		const license = this.selectedService.getLicense();
		return group.validateCanCreateActivity(license).isValid;
	}

	public getActivityCreationDisabledMessage (): string {
		const group = this.selectedService.getGroup();
		const license = this.selectedService.getLicense();
		const validationResult: ActivityValidationResult = group.validateCanCreateActivity(license);

		return validationResult.isValid ? '' : this.translate.instant(validationResult.errorKey);
	}

	public getTaskTitleAndDescription (activity: Activity, task: Task): TaskTitleAndDescription {
		const taskName = task.name ? escapeHtml(task.name) : null;

		switch (task.type) {
			case TaskType.Present:
				return this.getPresentTitleAndDescription(activity, taskName);
			case TaskType.Reflect:
				return {
					title: taskName || this.translate.instant('task-title_reflect'),
					description: this.translate.instant('task-description_reflect')
				};
			case TaskType.Submit:
				return {
					title: taskName || this.translate.instant('task-title_submit'),
					description: this.translate.instant('task-description_submit')
				};
			case TaskType.React:
				return {
					title: taskName || this.translate.instant('task-title_react'),
					description: this.translate.instant('task-description_react')
				};
			case TaskType.Review:
				return {
					title: taskName || this.translate.instant('task-title_review'),
					description: this.translate.instant('task-description_review')
				};
		}
	}

	private getPresentTitleAndDescription (activity: Activity, taskName: string = null): TaskTitleAndDescription {
		if (activity.has_response_media) {
			if (activity.live_session_enabled && !activity.has_single_recording_attempt) {
				return {
					title: taskName || this.translate.instant('task-title_present-live'),
					description: this.translate.instant('task-description_present-live')
				};
			}

			if (activity.has_single_recording_attempt) {
				return {
					title: taskName || this.translate.instant('task-title_present-single-attempt'),
					description: escapeHtml(this.translate.instant('task-description_present-single-attempt'))
				};
			}

			return {
				title: taskName || this.translate.instant('task-title_present-standard'),
				description: this.translate.instant('task-description_present-standard')
			};
		}

		const template = this.ActivityTemplateModel.model(activity.template);

		if (template.isActive('source_media')) {
			return {
				title: taskName || this.translate.instant('task-title_present-comment-only'),
				description: this.translate.instant('task-description_present-comment-only')
			};
		}

		return {
			title: taskName || this.translate.instant('task-title_present-conversation-board'),
			description: this.translate.instant('task-description_present-conversation-board')
		};
	}
}
